import { useState } from "react"
import { Navigate } from "@tanstack/react-router"
import { z } from "zod"

import {
	AlertDialog,
	Badge,
	Button,
	Form,
	FormInput,
	IntegrationTemplate,
	Loader,
	SubmitButton,
	Switch,
	useZodForm,
} from "@productlane/ui"
import { SentryIcon } from "@productlane/ui/src/icons/project/sentry-icon"

import type { RouterOutputs } from "@/lib/api"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { IntegrationLayout } from "./integration-layout"
import { useSentryStatus } from "./registry"

type SentrySettings = NonNullable<
	RouterOutputs["sentryIntegration"]["status"]["settings"]
>

const connectSchema = z.object({
	token: z.string().trim().min(1, "Paste the integration token"),
	baseUrl: z
		.string()
		.trim()
		.refine((value) => value === "" || /^https:\/\//i.test(value), {
			message: "Use an https address",
		}),
})

export function SentryIntegrationPage() {
	const statusQuery = useSentryStatus()

	if (statusQuery.isLoading) {
		return (
			<div className="flex justify-center">
				<Loader />
			</div>
		)
	}
	// Internal only: every other workspace lands back on the list.
	if (!statusQuery.data?.available) {
		return <Navigate to="/settings/integrations" replace />
	}
	return <SentryForm settings={statusQuery.data.settings} />
}

function SentryForm({ settings }: { settings: SentrySettings | null }) {
	const utils = api.useUtils()
	const [disconnectOpen, setDisconnectOpen] = useState(false)
	const refresh = () => utils.sentryIntegration.status.invalidate()
	const onError = (error: { message: string }) =>
		toast.error({ title: "Sentry", description: error.message })

	const connect = api.sentryIntegration.connect.useMutation({
		onSuccess: refresh,
		onError,
	})
	const disconnect = api.sentryIntegration.disconnect.useMutation({
		onSuccess: refresh,
		onError,
	})
	const setProjects = api.sentryIntegration.setProjects.useMutation({
		onSuccess: refresh,
		onError,
	})
	const refreshProjects = api.sentryIntegration.refreshProjects.useMutation({
		onSuccess: refresh,
		onError,
	})

	const form = useZodForm({
		schema: connectSchema,
		defaultValues: { token: "", baseUrl: "" },
	})

	const toggleProject = (projectId: string, enabled: boolean) => {
		if (!settings) return
		const next = enabled
			? [...settings.projectIds, projectId]
			: settings.projectIds.filter((id) => id !== projectId)
		setProjects.mutate({ projectIds: next })
	}

	return (
		<>
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<SentryIcon className="h-8 w-8" />}
				name={
					<div className="flex flex-wrap items-center gap-2 font-medium">
						Sentry integration
						{settings ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : null}
					</div>
				}
				description="Connect a Sentry organization and choose which projects Productlane can read."
				button={
					settings ? (
						<Button
							isLoading={disconnect.isLoading}
							onClick={() => setDisconnectOpen(true)}
						>
							Disconnect
						</Button>
					) : undefined
				}
				instructions={
					settings ? (
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<div className="text-sm font-medium">
									Projects in{" "}
									{settings.organizationSlug ?? settings.organizationId}
								</div>
								<Button
									variant="ghost"
									isLoading={refreshProjects.isLoading}
									onClick={() => refreshProjects.mutate()}
								>
									Refresh projects
								</Button>
							</div>
							<SettingsContainer>
								{settings.projects.length === 0 ? (
									<div className="text-sm text-secondary">
										No projects found in this organization.
									</div>
								) : (
									settings.projects.map((project, index) => (
										<SettingItem
											key={project.id}
											className={index === 0 ? undefined : "pt-4"}
											title={project.name}
											description={project.slug}
											action={
												<Switch
													name={`sentry-project-${project.id}`}
													checked={settings.projectIds.includes(project.id)}
													onCheckedChange={(checked) =>
														toggleProject(project.id, checked)
													}
												/>
											}
										/>
									))
								)}
							</SettingsContainer>
						</div>
					) : (
						<Form
							form={form}
							onSubmit={(values: z.infer<typeof connectSchema>) => {
								connect.mutate({
									token: values.token,
									baseUrl: values.baseUrl || undefined,
								})
								analytics.track("connect-sentry")
							}}
							className="space-y-4"
						>
							<div className="text-sm font-medium">Setup</div>
							<SettingsContainer className="space-y-4 divide-y-0">
								<div className="text-sm leading-relaxed text-secondary">
									In Sentry, open Settings, then Custom Integrations, and create
									an Internal Integration with the{" "}
									<span className="font-mono text-primary">event:read</span> and{" "}
									<span className="font-mono text-primary">org:read</span>{" "}
									scopes. Paste its token below.
								</div>
								<FormInput
									name="token"
									label="Integration token"
									placeholder="sntrys_..."
									type="password"
								/>
								<FormInput
									name="baseUrl"
									label="Self-hosted address (optional)"
									placeholder="https://sentry.example.com"
								/>
								<div className="flex justify-end">
									<SubmitButton isLoading={connect.isLoading}>
										Connect
									</SubmitButton>
								</div>
							</SettingsContainer>
						</Form>
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={disconnectOpen}
				onOpenChange={setDisconnectOpen}
				confirmText="Disconnect"
				title="Disconnect Sentry?"
				description="Productlane stops reading from this Sentry organization and forgets its token."
				onCancel={() => setDisconnectOpen(false)}
				onConfirm={() => {
					analytics.track("disconnect-sentry")
					disconnect.mutate()
					setDisconnectOpen(false)
				}}
			/>
		</>
	)
}
